import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback } from 'react';
import OrderState from '~/constants/common/OrderState';
import { useDeferredAction } from '~/context/common/DeferredActionContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import {
  useClosedAccountModal,
  useOpenAccountModal
} from '~/hooks/common/accountModal/useAccountModal';
import { useGetRcbUserPurchasingGetter } from '~/hooks/vdp/services/useGetUserPurchasing';
import { type PurchaseEventPayload } from '~/native/common/contracts';

type OnContinue = () => void;

export default function useLogInAndStartPurchase(
  eventPayload: PurchaseEventPayload
) {
  const isLoggedIn = useIsLoggedIn();
  const openAccountModal = useOpenAccountModal();
  const closedAccountModal = useClosedAccountModal();
  const getUserPurchasing = useGetRcbUserPurchasingGetter();
  const { executeOrQueueAccountModalAction } = useDeferredAction();

  return useCallback(
    (confirmContinue: OnContinue, onContinue: OnContinue) => () => {
      if (isLoggedIn) return onContinue();

      // Queue or execute account modal opening
      // The hook handles all logic: logged-in users, native users, and modal ready state
      executeOrQueueAccountModalAction(() => {
        openAccountModal({
          callerIdentifier: 'StartPurchase',
          nativeOptions: {
            eventPayload,
            eventDispatch: DispatcherEvent.StartPurchase
          },
          onSuccess: async () => {
            const purchaseResult = await getUserPurchasing();

            closedAccountModal('submit');

            if (purchaseResult?.orderState === OrderState.Reserved) {
              confirmContinue();
            } else {
              onContinue();
            }
          }
        });
      });
    },
    [
      closedAccountModal,
      eventPayload,
      executeOrQueueAccountModalAction,
      getUserPurchasing,
      isLoggedIn,
      openAccountModal
    ]
  );
}
